import { type ReactNode, useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Spinner } from '@/components/atoms/Spinner';

/**
 * 어드민 페이지 라우트 가드.
 * - 비로그인 → '/' 로 리다이렉트 (LoginModal 트리거 패턴은 기존 AuthGuard 와 동일)
 * - 로그인 + admin_role 없음 → '/' 로 리다이렉트
 * - admin_role 있음 + 권한 부족 → 안내 화면
 *
 * 역할 매핑 (backend requireAdminRole 와 동일):
 *   readonly == viewer (level 1) < content (level 2) < super (level 3)
 *
 * 사용:
 *   <Route element={<RequireAdmin />}>             // viewer 이상
 *   <Route element={<RequireAdmin minRole="super" />}>
 */

export type AdminRole = 'super' | 'content' | 'viewer' | 'readonly';

const ROLE_LEVEL: Record<string, number> = {
  readonly: 1,
  viewer: 1,
  content: 2,
  super: 3,
};

interface RequireAdminProps {
  minRole?: AdminRole;
  children?: ReactNode;
}

export function RequireAdmin({ minRole = 'viewer', children }: RequireAdminProps) {
  const { isLoggedIn, me, authReady } = useAuthStore();
  const navigate = useNavigate();

  // 첫 /users/me 응답 도착 전에는 라우트 결정 보류 (스피너)
  // 캐시된 me.adminRole 이 stale 인 경우 (로그인 직후 등) 라우트가 잘못 리다이렉트되는 것 방지
  useEffect(() => {
    if (authReady && !isLoggedIn) navigate('/', { replace: true });
  }, [authReady, isLoggedIn, navigate]);

  if (!authReady) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isLoggedIn) return null;

  // 어드민 권한 자체 없음 → 홈
  if (!me?.adminRole) {
    return <Navigate to="/" replace />;
  }

  const required = ROLE_LEVEL[minRole] ?? 1;
  const userLevel = ROLE_LEVEL[me.adminRole] ?? 0;

  if (userLevel < required) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 text-center">
        <div>
          <p className="text-lg font-semibold text-[var(--color-text)]">권한이 부족해요</p>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            이 페이지를 보려면 {minRole} 권한이 필요해요.
          </p>
        </div>
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet />;
}
